import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link, Outlet } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight, Menu, X } from "lucide-react";
import { useEffect } from "react";

export const Layout = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const openApp = () => {
    window.open(
      process.env.REACT_APP_FRONTEND_APP_URL,
      "_blank",
      "noopener,noreferrer"
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col relative">
      {/* Scrollbar styles remain the same */}
      <style>
        {`
          ::-webkit-scrollbar {
            display: none;
          }
          * {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>

      {/* Animated background gradients remain the same */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-blue-500/10 to-transparent rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            rotate: [360, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
          className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-cyan-500/10 to-transparent rounded-full blur-3xl"
        />
      </div>

      {/* Modified Navigation */}
      <nav className="sticky top-0 z-50 bg-gray-800/50 backdrop-blur-xl border-b border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            {/* Logo - Simplified for mobile */}
            <motion.div
              className="flex items-center flex-shrink-0"
              whileHover={{ scale: 1.02 }}
            >
              <Link to="/" className="flex items-center">
                <div className="flex items-center">
                  <div className="relative">
                    <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg blur opacity-40"></div>
                    <div className="relative h-8 w-8 md:h-10 md:w-10 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 flex items-center justify-center border border-blue-400/30 shadow-lg">
                      <span className="text-white font-bold text-lg md:text-xl">
                        S
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col ml-2 md:ml-3 -space-y-1">
                    <div className="flex items-center">
                      <span className="text-sm md:text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-cyan-300">
                        Sakshatkar
                      </span>
                      <span className="ml-1 text-sm md:text-lg font-bold text-cyan-400">
                        AI
                      </span>
                    </div>
                    <div className="relative hidden md:block">
                      <span className="text-[0.65rem] text-gray-400 tracking-wider font-light italic">
                        "Prepare • Excel • Get Hired"
                      </span>
                      <motion.div
                        className="absolute bottom-0 left-0 h-[1px] bg-gradient-to-r from-blue-500/50 to-cyan-400/50"
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          repeatType: "reverse",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => navigate("/features")}
                className="px-4 py-2 text-gray-300 hover:text-white rounded-lg hover:bg-gray-700/50"
              >
                Features
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={() => navigate("/about")}
                className="px-4 py-2 text-gray-300 hover:text-white rounded-lg hover:bg-gray-700/50"
              >
                About
              </motion.button>
              <Button
                onClick={openApp}
                className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500"
              >
                Launch App
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden">
              <button
                onClick={toggleMenu}
                className="p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-700/50"
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="md:hidden"
              >
                <div className="px-2 pt-2 pb-3 space-y-1">
                  <button
                    onClick={() => {
                      navigate("/features");
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white rounded-md hover:bg-gray-700/50"
                  >
                    Features
                  </button>
                  <button
                    onClick={() => {
                      navigate("/about");
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white rounded-md hover:bg-gray-700/50"
                  >
                    About
                  </button>
                  <Button
                    onClick={() => {
                      openApp();
                      setIsMenuOpen(false);
                    }}
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500"
                  >
                    Launch App
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1 relative overflow-y-auto">
        <Outlet />
      </main>

      {/* Version indicator */}
      {/* <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed bottom-4 right-4 text-xs text-gray-500"
      >
        v1.0.0
      </motion.div> */}
    </div>
  );
};

export default Layout;
